// ---------------------------------------------------------------------------
// SAP Integration Service — Connection management, profile CRUD, mapping
// configuration, and sync job orchestration.
// ---------------------------------------------------------------------------

import type {
  SapSystemConfig,
  SapIntegrationProfile,
  SapMappingGroup,
  FieldMapping,
  SapSyncJob,
  SyncDomain,
  SyncDirection,
  SapIntegrationDashboard,
  SapConnectionStatus,
  SapCostElementMaster,
  SapWbsElementMaster,
} from '@/types/sap-integration'
import * as sapSb from '@/services/sapSupabaseService'
import { timesheetDataService, bootstrapReferenceData } from '@/modules/timesheet/services/timesheetDataService'
import { fullyBurdenedRate } from '@/modules/timesheet/lib/billingVisibility'
import { aggregateLaborForSapPosting, type LaborLineForSap, type SapLaborPosting } from '@/services/sapLaborPosting'

function isSupabaseMode(): boolean {
  return (import.meta.env.VITE_DATA_SOURCE as string) === 'supabase'
}

// ---------------------------------------------------------------------------
// In-memory session state (used for mock mode; Supabase mode queries live)
// ---------------------------------------------------------------------------

let sapSystems: SapSystemConfig[] = []
let integrationProfiles: SapIntegrationProfile[] = []
let syncJobs: SapSyncJob[] = []
let costElementMasters: SapCostElementMaster[] = []
let wbsElementMasters: SapWbsElementMaster[] = []

let initialized = false

// ---------------------------------------------------------------------------
// Initialization — loads from Supabase or mock JSON on first access
// ---------------------------------------------------------------------------

async function ensureInitialized(): Promise<void> {
  if (initialized) return
  if (isSupabaseMode()) {
    try {
      const [systems, profiles, jobs] = await Promise.all([
        sapSb.getSapSystems(),
        sapSb.getIntegrationProfiles(),
        sapSb.getSyncJobs(),
      ])
      sapSystems = systems
      integrationProfiles = profiles
      syncJobs = jobs
      // Master data loaded lazily per-profile, not at init
    } catch (e) {
      console.warn('[sap] Supabase init error:', e)
    }
  } else {
    try {
      const res = await fetch('/mockdata/sap-integration/sap-systems.json')
      if (res.ok) {
        const data = await res.json()
        sapSystems = data.sapSystems ?? []
        integrationProfiles = data.integrationProfiles ?? []
        syncJobs = data.syncJobs ?? []
        costElementMasters = data.costElementMasters ?? []
        wbsElementMasters = data.wbsElementMasters ?? []
      }
    } catch {
      // Mock data not available — start with empty state
    }
  }
  initialized = true
}

// ---------------------------------------------------------------------------
// SAP System Connections
// ---------------------------------------------------------------------------

export async function getSapSystems(): Promise<SapSystemConfig[]> {
  await ensureInitialized()
  return [...sapSystems]
}

export async function getSapSystemById(id: string): Promise<SapSystemConfig | null> {
  await ensureInitialized()
  return sapSystems.find(s => s.id === id) ?? null
}

export async function createSapSystem(config: Omit<SapSystemConfig, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'lastConnectedAt' | 'lastErrorMessage'>): Promise<SapSystemConfig> {
  await ensureInitialized()
  const system: SapSystemConfig = {
    ...config,
    id: crypto.randomUUID(),
    status: 'Pending',
    lastConnectedAt: null,
    lastErrorMessage: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  sapSystems.push(system)
  return system
}

export async function updateSapSystem(id: string, updates: Partial<SapSystemConfig>): Promise<SapSystemConfig | null> {
  await ensureInitialized()
  const idx = sapSystems.findIndex(s => s.id === id)
  if (idx === -1) return null
  const existing = sapSystems[idx]!
  sapSystems[idx] = { ...existing, ...updates, updatedAt: new Date().toISOString() }
  return sapSystems[idx]!
}

export async function testSapConnection(id: string): Promise<{ success: boolean; message: string }> {
  await ensureInitialized()
  const system = sapSystems.find(s => s.id === id)
  if (!system) return { success: false, message: 'System not found' }

  // In production, this would make an actual OData $metadata request
  // For now, simulate a successful connection test
  const now = new Date().toISOString()
  const status: SapConnectionStatus = 'Connected'
  await updateSapSystem(id, { status, lastConnectedAt: now, lastErrorMessage: null })
  return { success: true, message: `Connected to ${system.name} (${system.systemId} client ${system.client})` }
}

// ---------------------------------------------------------------------------
// Integration Profiles
// ---------------------------------------------------------------------------

export async function getIntegrationProfiles(filters?: { projectId?: string; portfolioId?: string }): Promise<SapIntegrationProfile[]> {
  await ensureInitialized()
  let result = [...integrationProfiles]
  if (filters?.projectId) result = result.filter(p => p.projectId === filters.projectId)
  if (filters?.portfolioId) result = result.filter(p => p.portfolioId === filters.portfolioId)
  return result
}

export async function getIntegrationProfileById(id: string): Promise<SapIntegrationProfile | null> {
  await ensureInitialized()
  return integrationProfiles.find(p => p.id === id) ?? null
}

export async function createIntegrationProfile(
  profile: Omit<SapIntegrationProfile, 'id' | 'createdAt' | 'updatedAt' | 'mappingGroups'>
): Promise<SapIntegrationProfile> {
  await ensureInitialized()
  const newProfile: SapIntegrationProfile = {
    ...profile,
    id: crypto.randomUUID(),
    mappingGroups: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  integrationProfiles.push(newProfile)
  return newProfile
}

export async function updateIntegrationProfile(id: string, updates: Partial<SapIntegrationProfile>): Promise<SapIntegrationProfile | null> {
  await ensureInitialized()
  const idx = integrationProfiles.findIndex(p => p.id === id)
  if (idx === -1) return null
  const existing = integrationProfiles[idx]!
  integrationProfiles[idx] = { ...existing, ...updates, updatedAt: new Date().toISOString() }
  return integrationProfiles[idx]!
}

export async function cloneProfileAsTemplate(sourceId: string, newName: string): Promise<SapIntegrationProfile | null> {
  const source = await getIntegrationProfileById(sourceId)
  if (!source) return null
  const cloned: SapIntegrationProfile = {
    ...source,
    id: crypto.randomUUID(),
    name: newName,
    projectId: null,
    portfolioId: null,
    isTemplate: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    mappingGroups: source.mappingGroups.map(g => ({
      ...g,
      id: crypto.randomUUID(),
      fieldMappings: g.fieldMappings.map(f => ({ ...f, id: crypto.randomUUID() })),
    })),
  }
  integrationProfiles.push(cloned)
  return cloned
}

// ---------------------------------------------------------------------------
// Mapping Groups & Field Mappings
// ---------------------------------------------------------------------------

export async function getMappingGroups(profileId: string): Promise<SapMappingGroup[]> {
  const profile = await getIntegrationProfileById(profileId)
  return profile?.mappingGroups ?? []
}

export async function addMappingGroup(
  profileId: string,
  group: Omit<SapMappingGroup, 'id' | 'profileId' | 'fieldMappings'>
): Promise<SapMappingGroup | null> {
  await ensureInitialized()
  const idx = integrationProfiles.findIndex(p => p.id === profileId)
  if (idx === -1) return null
  const newGroup: SapMappingGroup = {
    ...group,
    id: crypto.randomUUID(),
    profileId,
    fieldMappings: [],
  }
  integrationProfiles[idx]!.mappingGroups.push(newGroup)
  integrationProfiles[idx]!.updatedAt = new Date().toISOString()
  return newGroup
}

export async function updateMappingGroup(
  profileId: string,
  groupId: string,
  updates: Partial<SapMappingGroup>
): Promise<SapMappingGroup | null> {
  await ensureInitialized()
  const profile = integrationProfiles.find(p => p.id === profileId)
  if (!profile) return null
  const gIdx = profile.mappingGroups.findIndex(g => g.id === groupId)
  if (gIdx === -1) return null
  const existingGroup = profile.mappingGroups[gIdx]!
  profile.mappingGroups[gIdx] = { ...existingGroup, ...updates }
  profile.updatedAt = new Date().toISOString()
  return profile.mappingGroups[gIdx]!
}

export async function addFieldMapping(
  profileId: string,
  groupId: string,
  mapping: Omit<FieldMapping, 'id' | 'mappingGroupId'>
): Promise<FieldMapping | null> {
  await ensureInitialized()
  const profile = integrationProfiles.find(p => p.id === profileId)
  if (!profile) return null
  const group = profile.mappingGroups.find(g => g.id === groupId)
  if (!group) return null
  const newMapping: FieldMapping = {
    ...mapping,
    id: crypto.randomUUID(),
    mappingGroupId: groupId,
  }
  group.fieldMappings.push(newMapping)
  profile.updatedAt = new Date().toISOString()
  return newMapping
}

export async function updateFieldMapping(
  profileId: string,
  groupId: string,
  mappingId: string,
  updates: Partial<FieldMapping>
): Promise<FieldMapping | null> {
  await ensureInitialized()
  const profile = integrationProfiles.find(p => p.id === profileId)
  if (!profile) return null
  const group = profile.mappingGroups.find(g => g.id === groupId)
  if (!group) return null
  const mIdx = group.fieldMappings.findIndex(m => m.id === mappingId)
  if (mIdx === -1) return null
  const existingMapping = group.fieldMappings[mIdx]!
  group.fieldMappings[mIdx] = { ...existingMapping, ...updates }
  profile.updatedAt = new Date().toISOString()
  return group.fieldMappings[mIdx]!
}

export async function deleteFieldMapping(profileId: string, groupId: string, mappingId: string): Promise<boolean> {
  await ensureInitialized()
  const profile = integrationProfiles.find(p => p.id === profileId)
  if (!profile) return false
  const group = profile.mappingGroups.find(g => g.id === groupId)
  if (!group) return false
  const before = group.fieldMappings.length
  group.fieldMappings = group.fieldMappings.filter(m => m.id !== mappingId)
  profile.updatedAt = new Date().toISOString()
  return group.fieldMappings.length < before
}

// ---------------------------------------------------------------------------
// Sync Jobs
// ---------------------------------------------------------------------------

export async function getSyncJobs(profileId?: string, domain?: SyncDomain): Promise<SapSyncJob[]> {
  await ensureInitialized()
  let result = [...syncJobs]
  if (profileId) result = result.filter(j => j.profileId === profileId)
  if (domain) result = result.filter(j => j.domain === domain)
  return result.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
}

export async function getSyncJobById(id: string): Promise<SapSyncJob | null> {
  await ensureInitialized()
  return syncJobs.find(j => j.id === id) ?? null
}

export async function triggerSync(
  profileId: string,
  domain: SyncDomain,
  direction: SyncDirection,
  triggeredByUser: string
): Promise<SapSyncJob> {
  await ensureInitialized()
  const job: SapSyncJob = {
    id: crypto.randomUUID(),
    profileId,
    domain,
    status: 'Queued',
    direction,
    triggeredBy: 'manual',
    triggeredByUser,
    recordsRead: 0,
    recordsCreated: 0,
    recordsUpdated: 0,
    recordsSkipped: 0,
    recordsErrored: 0,
    startedAt: new Date().toISOString(),
    completedAt: null,
    durationMs: null,
    errors: [],
  }
  syncJobs.push(job)
  return job
}

// ---------------------------------------------------------------------------
// #63 — Post approved timesheet LABOR to SAP (the timesheet→SAP gap).
// Reads approved submissions' entries, resolves each to its WBS (off the charge
// code) + burdened cost (hours × fully-burdened rate), rolls them up into SAP
// cost-line postings, and records a completed LaborTime sync job. Mock-sync demo
// pattern (no live SAP) — same as the rest of this surface.
// ---------------------------------------------------------------------------

export interface LaborPostResult {
  job: SapSyncJob
  postings: SapLaborPosting[]
  skippedCount: number
}

export async function postTimesheetLaborToSap(
  profileId: string,
  triggeredByUser: string,
): Promise<LaborPostResult> {
  await ensureInitialized()
  // Bootstrap the Supabase reference set so charge codes resolve from the live
  // `charge_lines` (ids like `cl_sol_*` / `cl_mdc2_*`, each carrying a real WBS
  // via its work-authorization) — the same source the rest of the app uses.
  // The entries' charge_code_id values ARE those charge_lines ids, so they
  // resolve and post; the few legacy `ts-cc-*` entries fall back to the seed
  // inside getChargeCode. (Before this, the resolver used the bundled seed,
  // which lacks the `cl_*` lines, so most labor was skipped — the thin export.)
  await bootstrapReferenceData()
  const submissions = (await timesheetDataService.getSubmissions()).filter((s) => s.status === 'approved')
  const lines: LaborLineForSap[] = []
  for (const sub of submissions) {
    const emp = timesheetDataService.getEmployee(sub.employeeId)
    // Burdened (wrap-loaded) labor cost — what a SAP CO actual line represents.
    const rate = emp ? (fullyBurdenedRate(emp, 'GlobalAdmin') ?? 0) : 0
    const entries = await timesheetDataService.getEntriesForWeek(sub.employeeId, sub.weekStart)
    for (const e of entries) {
      const cc = timesheetDataService.getChargeCode(e.chargeCodeId)
      lines.push({
        wbs: cc?.wbs ?? null,
        // indirect-pool charge codes carry their pool as the cost element;
        // direct codes fall back to the default labor cost element in the engine.
        costElement: cc?.pool ?? null,
        date: e.date,
        hours: e.hours,
        cost: Math.round(e.hours * rate * 100) / 100,
      })
    }
  }

  const { postings, skipped } = aggregateLaborForSapPosting(lines)
  const now = new Date().toISOString()
  const jobFields: Omit<SapSyncJob, 'id'> = {
    profileId,
    domain: 'LaborTime',
    status: 'Completed',
    direction: 'tessaract_to_sap',
    triggeredBy: 'manual',
    triggeredByUser,
    recordsRead: lines.length,
    recordsCreated: postings.length,
    recordsUpdated: 0,
    recordsSkipped: skipped.length,
    recordsErrored: 0,
    startedAt: now,
    completedAt: now,
    durationMs: 0,
    errors: [],
  }
  // Write where the page READS: the deployed demo runs in supabase mode, so the
  // job must land in sap_sync_jobs (not just the in-memory mock array) to appear
  // in Sync History. Mock mode keeps the in-memory job.
  let job: SapSyncJob
  if (isSupabaseMode()) {
    job = await sapSb.createSyncJob(jobFields)
  } else {
    job = { id: crypto.randomUUID(), ...jobFields }
    syncJobs.push(job)
  }
  return { job, postings, skippedCount: skipped.length }
}

// ---------------------------------------------------------------------------
// Master Data
// ---------------------------------------------------------------------------

export async function getCostElementMasters(_profileId: string): Promise<SapCostElementMaster[]> {
  await ensureInitialized()
  return costElementMasters.filter(m => m === m) // TODO: filter by profileId when mock data includes it
}

export async function getWbsElementMasters(_profileId: string): Promise<SapWbsElementMaster[]> {
  await ensureInitialized()
  return wbsElementMasters.filter(m => m === m) // TODO: filter by profileId when mock data includes it
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export async function getIntegrationDashboard(profileId: string): Promise<SapIntegrationDashboard | null> {
  const profile = await getIntegrationProfileById(profileId)
  if (!profile) return null
  const system = await getSapSystemById(profile.sapSystemId)
  if (!system) return null
  const jobs = await getSyncJobs(profileId)

  const domainKeys: SyncDomain[] = ['CostActuals', 'Commitments', 'WbsStructure', 'Budget', 'Forecast', 'LaborTime', 'MasterData', 'Revenue']
  const lastSyncByDomain = Object.fromEntries(
    domainKeys.map(d => [d, jobs.find(j => j.domain === d) ?? null])
  ) as Record<SyncDomain, SapSyncJob | null>

  const totalFields = profile.mappingGroups.reduce((sum, g) => sum + g.fieldMappings.length, 0)
  const mappedFields = profile.mappingGroups.reduce(
    (sum, g) => sum + g.fieldMappings.filter(f => f.enabled).length, 0
  )

  return {
    profileId: profile.id,
    profileName: profile.name,
    sapSystemName: system.name,
    connectionStatus: system.status,
    lastSyncByDomain,
    totalRecordsSynced: jobs.reduce((sum, j) => sum + j.recordsCreated + j.recordsUpdated, 0),
    totalErrors: jobs.reduce((sum, j) => sum + j.recordsErrored, 0),
    nextScheduledSync: null, // Computed from sync schedule in production
    mappingCoverage: {
      totalFields,
      mappedFields,
      unmappedFields: totalFields - mappedFields,
    },
  }
}

// ---------------------------------------------------------------------------
// Default Mapping Templates — pre-built mapping groups for common SAP entities
// ---------------------------------------------------------------------------

/** Returns default mapping groups for Phase 1 (Cost Actuals from CO). */
export function getDefaultCostActualsMappings(): Omit<SapMappingGroup, 'id' | 'profileId'>[] {
  return [
    {
      name: 'Cost Actuals',
      description: 'CO line items (COEP/COSP) → Tessaract periodic actuals',
      sapEntity: 'COEP',
      sapEntityLabel: 'CO Line Items — Actual',
      tessaractEntity: 'EvmPeriodicActual',
      direction: 'sap_to_tessaract',
      keyMapping: {
        tessaractKey: 'wbsId',
        sapKey: 'WBSElement',
        matchStrategy: 'prefix',
        lookupTable: null,
      },
      fieldMappings: [
        {
          id: 'default-cost-1',
          mappingGroupId: '',
          tessaractField: 'ac',
          tessaractFieldLabel: 'Actual Cost',
          sapField: 'WTG001',
          sapFieldLabel: 'Value in CO Area Currency',
          sapFieldType: 'Edm.Decimal',
          direction: 'read',
          transform: null,
          required: true,
          defaultValue: null,
          enabled: true,
          sortOrder: 1,
        },
        {
          id: 'default-cost-2',
          mappingGroupId: '',
          tessaractField: 'hoursWorked',
          tessaractFieldLabel: 'Hours Worked',
          sapField: 'MSG001',
          sapFieldLabel: 'Total Quantity',
          sapFieldType: 'Edm.Decimal',
          direction: 'read',
          transform: null,
          required: false,
          defaultValue: 0,
          enabled: true,
          sortOrder: 2,
        },
        {
          id: 'default-cost-3',
          mappingGroupId: '',
          tessaractField: 'period',
          tessaractFieldLabel: 'Tessaract Period',
          sapField: 'PERIO',
          sapFieldLabel: 'Posting Period',
          sapFieldType: 'Edm.String',
          direction: 'read',
          transform: {
            type: 'calculate',
            config: {
              type: 'calculate',
              formula: '`${record.GJAHR}-${String(parseInt(value)).padStart(2, "0")}`',
            },
          },
          required: true,
          defaultValue: null,
          enabled: true,
          sortOrder: 3,
        },
      ],
      enabled: true,
      sortOrder: 1,
    },
    {
      name: 'Commitments',
      description: 'Open purchase orders & requisitions (EKPO/EBAN) → Tessaract commitments',
      sapEntity: 'A_PurchaseOrder',
      sapEntityLabel: 'Purchase Orders',
      tessaractEntity: 'SapCommitmentLineItem',
      direction: 'sap_to_tessaract',
      keyMapping: {
        tessaractKey: 'wbsId',
        sapKey: 'WBSElement',
        matchStrategy: 'prefix',
        lookupTable: null,
      },
      fieldMappings: [
        {
          id: 'default-commit-1',
          mappingGroupId: '',
          tessaractField: 'committedAmount',
          tessaractFieldLabel: 'Committed Amount',
          sapField: 'NetOrderValueInTransacCrcy',
          sapFieldLabel: 'Net Order Value',
          sapFieldType: 'Edm.Decimal',
          direction: 'read',
          transform: null,
          required: true,
          defaultValue: null,
          enabled: true,
          sortOrder: 1,
        },
        {
          id: 'default-commit-2',
          mappingGroupId: '',
          tessaractField: 'invoicedAmount',
          tessaractFieldLabel: 'Invoiced Amount',
          sapField: 'InvoicedAmount',
          sapFieldLabel: 'Invoiced Amount',
          sapFieldType: 'Edm.Decimal',
          direction: 'read',
          transform: null,
          required: false,
          defaultValue: 0,
          enabled: true,
          sortOrder: 2,
        },
      ],
      enabled: true,
      sortOrder: 2,
    },
    {
      name: 'WBS Master Data',
      description: 'SAP WBS element master → Tessaract WBS reference data',
      sapEntity: 'A_WBSElement',
      sapEntityLabel: 'WBS Elements',
      tessaractEntity: 'SapWbsElementMaster',
      direction: 'sap_to_tessaract',
      keyMapping: {
        tessaractKey: 'wbsId',
        sapKey: 'WBSElementExternalID',
        matchStrategy: 'exact',
        lookupTable: null,
      },
      fieldMappings: [
        {
          id: 'default-wbs-1',
          mappingGroupId: '',
          tessaractField: 'description',
          tessaractFieldLabel: 'Description',
          sapField: 'WBSDescription',
          sapFieldLabel: 'WBS Description',
          sapFieldType: 'Edm.String',
          direction: 'read',
          transform: null,
          required: true,
          defaultValue: null,
          enabled: true,
          sortOrder: 1,
        },
        {
          id: 'default-wbs-2',
          mappingGroupId: '',
          tessaractField: 'systemStatus',
          tessaractFieldLabel: 'System Status',
          sapField: 'ProjectObjectStatusCode',
          sapFieldLabel: 'System Status',
          sapFieldType: 'Edm.String',
          direction: 'read',
          transform: {
            type: 'value_map',
            config: {
              type: 'value_map',
              mapping: { 'I0001': 'Created', 'I0002': 'Released', 'I0045': 'TechComplete', 'I0046': 'Closed' },
              unmappedStrategy: 'passthrough',
            },
          },
          required: true,
          defaultValue: null,
          enabled: true,
          sortOrder: 2,
        },
      ],
      enabled: true,
      sortOrder: 3,
    },
  ]
}

// ---------------------------------------------------------------------------
// Dassian PPC PTW Plan Push
// ---------------------------------------------------------------------------
// Bridges PPM PTW amounts -> SAP Dassian PPC planning. Called inline from
// createPtwForecastVersion when profile.pushPlanOnStartup === true.
//
// Architecture: PPM ptw_amounts (category_id, period 'YYYY-MM', amount)
//   -> category lookup in profile's 'PTW Plan' SapMappingGroup
//   -> POST /api/erp-sync/push-ppc-plan -> Vercel function -> ppm-s4-proxy CF
//   -> Cloud Connector -> Z_DEMO_RUNNER RunExecutor -> ZCL_M12_PPC_PLAN_EXEC
//      (validates, then /DSN/010_PLAN_DATA_INSERT per header)
// The earlier /api/s4/ptw-push target never existed server side.
// See [[reference-ppm-sap-proxy-chain]] for the proxy chain.
// ---------------------------------------------------------------------------

export interface PtwPushResult {
  status: 'ok' | 'error' | 'skipped'
  headers?: number
  details?: number
  plgnumStart?: string
  plgnumEnd?: string
  message?: string
}

/** Categorize a PTW row as cost / revenue / hours based on the cost element. */
function classifyKey(kstar: string): 'cost' | 'revenue' | 'hours' {
  if (!kstar) return 'hours'
  if (kstar.startsWith('Z6')) return 'cost'
  if (kstar.startsWith('Z8')) return 'revenue'
  // Treat unknown KSTARs as cost so the loader sees a deterministic PLTYPE
  return 'cost'
}

/**
 * Resolve a PTW category to a SAP cost element / activity type by consulting
 * the profile's 'PTW Plan' SapMappingGroup. Each FieldMapping has
 * tessaractField = category id and sapField = KSTAR (or LSTAR for hours).
 *
 * Returns null if no mapping is configured for the category — the caller
 * should skip the row and surface the unmapped categories.
 */
export function resolveCategoryToSap(
  profile: SapIntegrationProfile,
  categoryId: string,
): { kstar: string; lstar: string; meinh: string } | null {
  const group = profile.mappingGroups.find(
    g => g.tessaractEntity === 'PtwCategory' || g.name === 'PTW Plan',
  )
  if (!group) return null
  const mapping = group.fieldMappings.find(f => f.tessaractField === categoryId && f.enabled)
  if (!mapping) return null
  const sapField = mapping.sapField ?? ''
  // Convention: LSTARs start with a digit OR a non-Z letter; KSTARs start with Z6/Z8
  if (/^Z[68]/.test(sapField)) {
    return { kstar: sapField, lstar: '', meinh: 'USD' }
  }
  // Treat anything else as an activity type
  return { kstar: '', lstar: sapField, meinh: 'H' }
}

/** Push PTW amounts to SAP for a given profile. Returns the SAP response. */
export async function pushPtwToSap(args: {
  profile: SapIntegrationProfile
  ptwAmounts: Array<{ categoryId: string; period: string; amount: number }>
}): Promise<PtwPushResult> {
  const { profile, ptwAmounts } = args

  if (!profile.pushPlanOnStartup) {
    return { status: 'skipped', message: 'pushPlanOnStartup is off' }
  }
  const pspid = (profile.sapProjectDefinition ?? '').trim().toUpperCase()
  if (!pspid) {
    return { status: 'error', message: 'profile missing sapProjectDefinition (the SAP project key the plan loads against)' }
  }
  if (!profile.sapBaselineVersion || !profile.sapRevenueVersion) {
    return { status: 'error', message: 'profile missing baseline or revenue version' }
  }
  if (ptwAmounts.length === 0) {
    return { status: 'skipped', message: 'no PTW amounts to push' }
  }

  // PTW is project-level (no WBS): every row lands on the project element. Cost and
  // hours go to the baseline version, revenue to the PTW version (else the revenue
  // version). PPM stores period as 'YYYY-MM'; the runner wants year + month.
  const revenueVersion = (profile.sapPtwVersion || profile.sapRevenueVersion).toUpperCase()
  const baselineVersion = profile.sapBaselineVersion.toUpperCase()
  const rows: Array<{ posid: string; plversn: string; kstar: string; lstar: string; year: number; month: number; value: number }> = []
  const unmapped: string[] = []
  for (const a of ptwAmounts) {
    const mapped = resolveCategoryToSap(profile, a.categoryId)
    if (!mapped) {
      if (!unmapped.includes(a.categoryId)) unmapped.push(a.categoryId)
      continue
    }
    const [yyyy, mm] = a.period.split('-').map(s => parseInt(s, 10))
    if (!yyyy || !mm || !a.amount) continue
    rows.push({
      posid: pspid,
      plversn: classifyKey(mapped.kstar) === 'revenue' ? revenueVersion : baselineVersion,
      kstar: mapped.kstar,
      lstar: mapped.lstar,
      year: yyyy,
      month: mm,
      value: a.amount,
    })
  }

  if (rows.length === 0) {
    return {
      status: 'skipped',
      message: `no rows after category mapping (unmapped: ${unmapped.join(', ') || 'none'})`,
    }
  }

  // One call to the Dassian PPC runner (/api/erp-sync/push-ppc-plan ->
  // ZCL_M12_PPC_PLAN_EXEC). It validates every row before writing and, with
  // replace, swaps this project's rows for the versions sent.
  try {
    const versions = [...new Set(rows.map(r => r.plversn))]
    const res = await fetch('/api/erp-sync/push-ppc-plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pspid, versions, rows, replace: true }),
    })
    const json = await res.json().catch(() => ({})) as {
      ok?: boolean; headers?: number; details?: number; errors?: string[]; errorMessage?: string; message?: string
    }
    if (!res.ok || json.ok === false) {
      return { status: 'error', message: json.errorMessage || (json.errors ?? []).join('; ') || json.message || `HTTP ${res.status}` }
    }
    return {
      status: 'ok',
      headers: json.headers ?? 0,
      details: json.details ?? 0,
      message: unmapped.length > 0 ? `unmapped categories skipped: ${unmapped.join(', ')}` : undefined,
    }
  } catch (e) {
    return { status: 'error', message: e instanceof Error ? e.message : String(e) }
  }
}

